'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { UUID_RE } from '../dashboardRoutes'

/** Fired by BackofficeApp's navigateTo after its own pushState (which, unlike
 * Back/Forward, raises no popstate): a sidebar click on the board you are on
 * must close the detail the address bar no longer names. */
export const BACKOFFICE_NAVIGATE_EVENT = 'backoffice:navigate'

function splitPath(pathname: string): { base: string; id: string | null } {
  const trimmed = pathname.replace(/\/+$/, '')
  const i = trimmed.lastIndexOf('/')
  const last = trimmed.slice(i + 1)
  return UUID_RE.test(last) ? { base: trimmed.slice(0, i), id: last } : { base: trimmed, id: null }
}

/**
 * The URL of an item on a sibling board of the one the address bar shows:
 * on /ctt/domain-candidates/{candidate}, siblingDetailHref('unified', id) is
 * /ctt/unified/{id}. The brand (or /entries) prefix is kept, so a tenant's
 * link stays inside its own brand. For opening in a new tab: the target
 * board must be in dashboardRoutes' DETAIL_SLUGS.
 */
export function siblingDetailHref(slug: string, itemId: string): string {
  if (typeof window === 'undefined') return '#'
  const base = splitPath(window.location.pathname).base
  return `${base.slice(0, base.lastIndexOf('/'))}/${slug}/${itemId}`
}

/**
 * A board's open detail as the last path segment: /ctt/domain-candidates/{id}.
 * The [id] routes serve that URL with the board's shell, so a pasted link
 * opens straight into the item; this hook is the client half.
 *   - `id` -- the item the address bar names (read at mount, then on every
 *     Back/Forward and sidebar navigation).
 *   - `open(id)` -- pushes {base}/{id}.
 *   - `close()` -- Back when this tab pushed the detail (so the close button
 *     and the browser's Back do the same thing), else (a cold deep link: Back
 *     would leave the site) replaces the URL with the board's own.
 *   - `href(id)` -- the item's URL, for opening it in a new tab.
 */
export function useDetailPath() {
  const [id, setId] = useState<string | null>(() => (typeof window === 'undefined' ? null : splitPath(window.location.pathname).id))
  const pushedRef = useRef(false)

  useEffect(() => {
    const sync = () => {
      const next = splitPath(window.location.pathname).id
      if (!next) pushedRef.current = false
      setId(next)
    }
    window.addEventListener('popstate', sync)
    window.addEventListener(BACKOFFICE_NAVIGATE_EVENT, sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener(BACKOFFICE_NAVIGATE_EVENT, sync)
    }
  }, [])

  const href = useCallback((itemId: string) => `${splitPath(window.location.pathname).base}/${itemId}`, [])

  const open = useCallback((itemId: string) => {
    window.history.pushState(null, '', href(itemId))
    pushedRef.current = true
    setId(itemId)
  }, [href])

  const close = useCallback(() => {
    if (pushedRef.current) {
      window.history.back()           // popstate -> sync -> id null
      return
    }
    window.history.replaceState(null, '', splitPath(window.location.pathname).base)
    setId(null)
  }, [])

  return { id, open, close, href }
}
